import type { QueryClient } from '@tanstack/react-query';
import { createBrowserRouter, redirect, type RouteObject } from 'react-router';
import { RequirePermission, RequirePlatformAdmin } from '@/features/access-control';
import { ProfilePage } from '@/features/account';
import {
  AcceptInvitationPage,
  ForgotPasswordPage,
  homePathFor,
  LoginPage,
  loginLoader,
  requireSession,
  RequireAuth,
  ResetPasswordPage,
} from '@/features/auth';
import { type PartySection, paths } from '@/shared/lib/paths';
import { ForbiddenPage, NotFoundPage, RouteErrorPage, StandalonePage } from '@/shared/ui/feedback';
import { AppLayout } from './app-layout';

/** Erro fora do layout autenticado: sem o `main` do layout, a página traz o seu. */
const standaloneError = (
  <StandalonePage>
    <RouteErrorPage />
  </StandalonePage>
);

/**
 * Títulos: todas as contas (`/titles`), listagem e lançamento a pagar (`/payables`) e a receber
 * (`/receivables`), e detalhe.
 */
function titleRoutes(queryClient: QueryClient): RouteObject {
  const kinds = [
    { kind: 'PAYABLE', path: 'payables' },
    { kind: 'RECEIVABLE', path: 'receivables' },
  ] as const;
  return {
    element: <RequirePermission permission="titles:read" />,
    children: [
      ...kinds.map(({ kind, path }) => ({
        path,
        errorElement: <RouteErrorPage />,
        lazy: async () => {
          const { createTitlesRoute } = await import('@/features/titles');
          return createTitlesRoute(queryClient, kind);
        },
      })),
      {
        path: 'titles',
        errorElement: <RouteErrorPage />,
        lazy: async () => {
          const { createTitlesRoute } = await import('@/features/titles');
          return createTitlesRoute(queryClient);
        },
      },
      {
        path: 'titles/:titleId',
        errorElement: <RouteErrorPage />,
        lazy: async () => {
          const { createTitleDetailRoute } = await import('@/features/titles');
          return createTitleDetailRoute(queryClient);
        },
      },
      {
        element: <RequirePermission permission="titles:manage" />,
        children: [
          {
            path: 'titles/:titleId/edit',
            errorElement: <RouteErrorPage />,
            lazy: async () => {
              const { createEditTitleRoute } = await import('@/features/titles');
              return createEditTitleRoute(queryClient);
            },
          },
        ],
      },
      {
        element: <RequirePermission permission="titles:create" />,
        children: kinds.map(({ kind, path }) => ({
          path: `${path}/new`,
          errorElement: <RouteErrorPage />,
          lazy: async () => {
            const { createNewTitleRoute } = await import('@/features/titles');
            return createNewTitleRoute(kind);
          },
        })),
      },
    ],
  };
}

const PARTY_SECTION_NAMES: readonly PartySection[] = ['customers', 'suppliers'];

/**
 * Rotas de uma seção de clientes/fornecedores (`/customers` ou `/suppliers`): lista, novo
 * cadastro, importação e detalhe. As duas seções usam o mesmo cadastro (quem é "ambos" aparece
 * nas duas).
 */
function partySectionRoutes(queryClient: QueryClient, section: PartySection): RouteObject {
  return {
    element: <RequirePermission permission="parties:read" />,
    children: [
      {
        path: section,
        errorElement: <RouteErrorPage />,
        children: [
          {
            index: true,
            lazy: async () => {
              const { createPartiesRoute } = await import('@/features/parties');
              return createPartiesRoute(queryClient, section);
            },
          },
          {
            element: <RequirePermission permission="parties:manage" />,
            children: [
              {
                path: 'import',
                lazy: async () => {
                  const { createImportPartiesRoute } = await import('@/features/parties');
                  return createImportPartiesRoute(section);
                },
              },
            ],
          },
          {
            element: <RequirePermission permission="parties:create" />,
            children: [
              {
                path: 'new',
                lazy: async () => {
                  const { createNewPartyRoute } = await import('@/features/parties');
                  return createNewPartyRoute(section);
                },
              },
            ],
          },
          {
            path: ':partyId',
            lazy: async () => {
              const { createPartyRoute } = await import('@/features/parties');
              return createPartyRoute(queryClient, section);
            },
          },
        ],
      },
    ],
  };
}

/**
 * Telas do admin geral da plataforma (ADR-0021): organizações, os administradores de cada uma e
 * os admins gerais. Não dependem de permissão na organização.
 */
function platformRoutes(queryClient: QueryClient): RouteObject {
  return {
    path: 'platform',
    element: <RequirePlatformAdmin />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: 'organizations',
        children: [
          {
            index: true,
            lazy: async () => {
              const { createPlatformOrganizationsRoute } = await import('@/features/platform');
              return createPlatformOrganizationsRoute(queryClient);
            },
          },
          {
            path: 'new',
            lazy: async () => {
              const { createNewPlatformOrganizationRoute } = await import('@/features/platform');
              return createNewPlatformOrganizationRoute();
            },
          },
          {
            path: ':organizationId',
            lazy: async () => {
              const { createPlatformOrganizationRoute } = await import('@/features/platform');
              return createPlatformOrganizationRoute(queryClient);
            },
          },
        ],
      },
      {
        path: 'administrators',
        lazy: async () => {
          const { createPlatformAdministratorsRoute } = await import('@/features/platform');
          return createPlatformAdministratorsRoute(queryClient);
        },
      },
    ],
  };
}

/** Rotas da aplicação (React Router v7, modo data). */
export function createRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    { path: '/login', loader: loginLoader, Component: LoginPage, errorElement: standaloneError },
    { path: '/forgot-password', Component: ForgotPasswordPage, errorElement: standaloneError },
    { path: '/reset-password', Component: ResetPasswordPage, errorElement: standaloneError },
    {
      path: '/accept-invitation',
      Component: AcceptInvitationPage,
      errorElement: standaloneError,
    },
    {
      path: '/forbidden',
      element: (
        <StandalonePage>
          <ForbiddenPage />
        </StandalonePage>
      ),
    },
    {
      path: '/',
      loader: async ({ request }) => {
        await requireSession(request);
        return null;
      },
      Component: RequireAuth,
      errorElement: standaloneError,
      children: [
        {
          Component: AppLayout,
          errorElement: <RouteErrorPage />,
          children: [
            {
              index: true,
              loader: async ({ request }) => redirect(homePathFor(await requireSession(request))),
            },
            { path: 'account', Component: ProfilePage },
            { path: 'account/password', loader: () => redirect(paths.profile()) },
            {
              element: <RequirePermission permission="companies:read" />,
              children: [
                {
                  path: 'companies',
                  errorElement: <RouteErrorPage />,
                  children: [
                    {
                      index: true,
                      lazy: async () => {
                        const { createCompaniesRoute } = await import('@/features/companies');
                        return createCompaniesRoute(queryClient);
                      },
                    },
                    {
                      element: <RequirePermission permission="companies:manage" />,
                      children: [
                        {
                          path: 'new',
                          lazy: async () => {
                            const { createNewCompanyRoute } = await import('@/features/companies');
                            return createNewCompanyRoute();
                          },
                        },
                      ],
                    },
                    {
                      path: ':companyId',
                      lazy: async () => {
                        const { createCompanyRoute } = await import('@/features/companies');
                        return createCompanyRoute(queryClient);
                      },
                    },
                  ],
                },
              ],
            },
            ...PARTY_SECTION_NAMES.map((section) => partySectionRoutes(queryClient, section)),
            {
              element: <RequirePermission permission="income-statement:read" />,
              children: [
                {
                  path: 'income-statement',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createIncomeStatementRoute } =
                      await import('@/features/income-statement');
                    return createIncomeStatementRoute();
                  },
                },
              ],
            },
            {
              element: <RequirePermission permission="users:manage" />,
              children: [
                {
                  path: 'users',
                  errorElement: <RouteErrorPage />,
                  children: [
                    {
                      index: true,
                      lazy: async () => {
                        const { createUsersRoute } = await import('@/features/users');
                        return createUsersRoute(queryClient);
                      },
                    },
                    {
                      path: 'new',
                      lazy: async () => {
                        const { createNewUserRoute } = await import('@/features/users');
                        return createNewUserRoute();
                      },
                    },
                    {
                      path: ':userId',
                      lazy: async () => {
                        const { createUserRoute } = await import('@/features/users');
                        return createUserRoute(queryClient);
                      },
                    },
                  ],
                },
              ],
            },
            titleRoutes(queryClient),
            platformRoutes(queryClient),
            {
              element: <RequirePermission permission="ledger-accounts:read" />,
              children: [
                {
                  path: 'classifications',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createClassificationsRoute } = await import('@/features/catalogs');
                    return createClassificationsRoute(queryClient);
                  },
                },
              ],
            },
            {
              element: <RequirePermission permission="cost-centers:read" />,
              children: [
                {
                  path: 'cost-centers',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createCostCentersRoute } = await import('@/features/catalogs');
                    return createCostCentersRoute(queryClient);
                  },
                },
              ],
            },
            {
              element: <RequirePermission permission="titles:create" />,
              children: [
                {
                  path: 'imports',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createImportsRoute } = await import('@/features/imports');
                    return createImportsRoute();
                  },
                },
              ],
            },
            {
              element: <RequirePermission permission="bank-reconciliation:read" />,
              children: [
                {
                  path: 'bank-reconciliation',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createBankReconciliationRoute } =
                      await import('@/features/bank-reconciliation');
                    return createBankReconciliationRoute();
                  },
                },
              ],
            },
            {
              element: <RequirePermission permission="accounting-periods:read" />,
              children: [
                {
                  path: 'accounting-periods',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createAccountingPeriodsRoute } =
                      await import('@/features/accounting-periods');
                    return createAccountingPeriodsRoute();
                  },
                },
              ],
            },
            {
              element: <RequirePermission permission="bank-accounts:read" />,
              children: [
                {
                  path: 'bank-accounts',
                  errorElement: <RouteErrorPage />,
                  lazy: async () => {
                    const { createBankAccountsRoute } = await import('@/features/bank-accounts');
                    return createBankAccountsRoute();
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: (
        <StandalonePage>
          <NotFoundPage />
        </StandalonePage>
      ),
    },
  ];
}

/** Cria o router do navegador. */
export function createAppRouter(queryClient: QueryClient) {
  return createBrowserRouter(createRoutes(queryClient));
}
